import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type {
  HousingRelationshipAnalysis,
  HousingRelationshipResult,
  PeriodVerdict,
  RelationshipDirection,
  RelationshipStability
} from "../data/housingRelationshipAnalysis";

function correlation(value: number | null) {
  if (value == null || !Number.isFinite(value)) return "N/A";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}`;
}

function directionLabel(direction: RelationshipDirection) {
  if (direction === "positive") return "양(+) 방향";
  if (direction === "negative") return "음(-) 방향";
  if (direction === "mixed") return "약함/혼재";
  return "판단 보류";
}

function verdictLabel(verdict: PeriodVerdict) {
  if (verdict === "stable") return "기간에 따라 대체로 안정";
  if (verdict === "strengthening") return "최근 관계 강화";
  if (verdict === "weakening") return "최근 관계 약화";
  if (verdict === "sign_reversal") return "부호 반전 · 구조변화 후보";
  if (verdict === "unstable") return "기간에 민감";
  return "비교 표본 부족";
}


function stabilityLabel(value: RelationshipStability) {
  if (value === "stable") return "관계 안정";
  if (value === "weak") return "관계 약함";
  if (value === "lag_unstable") return "lag에 따라 불안정";
  if (value === "period_sensitive") return "기간에 민감";
  if (value === "policy_sensitive") return "정책국면에 민감";
  return "평가 표본 부족";
}

function policyResultLabel(result: HousingRelationshipResult["policyComparisons"][number]["result"]) {
  if (result === "sign_reversal") return "정책 전후 부호 반전 후보";
  if (result === "strengthened") return "정책 후 관계 강화";
  if (result === "weakened") return "정책 후 관계 약화";
  if (result === "stable") return "큰 변화 미확인";
  return "표본 부족";
}

function periodRange(start: string | null, end: string | null) {
  if (!start || !end) return "N/A";
  return `${start} ~ ${end}`;
}

function policyTiming(offsetMonths: number, basis: "effective" | "announced") {
  const verb = basis === "effective" ? "시행" : "발표";
  if (offsetMonths === 0) return `구조변화와 같은 달 ${verb}`;
  if (offsetMonths < 0) return `구조변화 ${Math.abs(offsetMonths)}개월 전 ${verb}`;
  return `구조변화 ${offsetMonths}개월 후 ${verb}`;
}

export default function HousingRelationshipAnalysisPanel({ analysis }: { analysis: HousingRelationshipAnalysis }) {
  const [selectedId, setSelectedId] = useState<string>(() => analysis.relationships[0]?.id ?? "");
  const selected = analysis.relationships.find((item) => item.id === selectedId) ?? analysis.relationships[0] ?? null;
  const rolling = useMemo(() => selected?.rolling.filter((point) => point.correlation != null) ?? [], [selected]);
  const recentCandidates = analysis.watchedPolicyCandidates.slice(0, 5);

  if (analysis.safety.status === "blocked" || !selected) {
    return (
      <section className="housing-structure-section">
        <header className="housing-h4-header">
          <div>
            <p className="kicker">H4.6 · REGIME & STRUCTURAL CHANGE</p>
            <h4>기간·정책국면·구조변화 자동분석</h4>
            <p>각 관계의 공통 관측기간이 더 쌓이면 전체(MAX)/최근 N년/정책국면 비교와 rolling 구조변화 탐지를 자동으로 시작합니다.</p>
          </div>
        </header>
        <div className="empty-state">현재 최대 분석 관측월 {analysis.safety.completeMonths}개월 · 구조변화 분석 최소 표본을 아직 충족하지 못했습니다.</div>
      </section>
    );
  }

  return (
    <section className="housing-structure-section">
      <header className="housing-h4-header housing-structure-header">
        <div>
          <p className="kicker">H4.6 · REGIME & STRUCTURAL CHANGE</p>
          <h4>관계가 기간이나 정책 때문에 달라졌나?</h4>
          <p>각 관계의 전체(MAX) 공통기간을 기준점으로 두고 최근 N년·현재/직전 정책국면을 자동 비교합니다. 24개월 rolling 관계가 크게 달라진 시점은 구조변화 후보로 찾은 뒤, 그 주변의 정책을 별도로 연결합니다.</p>
          <small>상단 1Y/3Y/5Y/10Y/MAX는 차트 표시기간입니다. H4.6은 별도로 각 관계의 전체(MAX)·최근 N년·정책국면을 동시에 계산합니다. 공식 가격지수는 RTMS 시작월에 잘리지 않습니다.</small>
        </div>
        <label className="housing-relationship-select">
          <span>관계 선택</span>
          <select value={selected.id} onChange={(event) => setSelectedId(event.target.value)}>
            {analysis.relationships.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
      </header>

      <div className={`housing-safety-card housing-safety-card--${analysis.safety.status}`}>
        <strong>분석 안전상태: {analysis.safety.status === "ready" ? "충분" : "주의"}</strong>
        <span>최대 분석 관측월 {analysis.safety.completeMonths}개월 · {periodRange(analysis.safety.startMonth, analysis.safety.endMonth)}</span>
        {analysis.safety.warnings.map((warning) => <small key={warning}>⚠ {warning}</small>)}
      </div>

      <div className="housing-structure-summary">
        <article>
          <span>기간 민감도</span>
          <strong>{verdictLabel(selected.periodVerdict)}</strong>
          <p>{selected.periodExplanation}</p>
        </article>
        <article>
          <span>관계 안정성</span>
          <strong>{stabilityLabel(selected.stability)}</strong>
          <p>{selected.stabilityExplanation}</p>
        </article>
        <article>
          <span>rolling 구조변화</span>
          <strong>{selected.structuralChanges.length ? `${selected.structuralChanges.length}개 후보` : "뚜렷한 후보 없음"}</strong>
          <p>한 시점의 최대 r가 아니라 {selected.rollingWindowMonths}개월 rolling 전이구간 대표 r의 변화로 탐색합니다.</p>
        </article>
        <article>
          <span>정책 Auto-Watcher</span>
          <strong>{analysis.policyWatchStatus === "ready" ? "공식 소스 정상" : analysis.policyWatchStatus === "partial" ? "일부 소스 정상" : "첫 수집/복구 대기"}</strong>
          <p>자동수집 후보 {analysis.watchedPolicyCandidates.length}건. 후보는 확정 숫자 레짐을 자동 덮어쓰지 않습니다.</p>
        </article>
        <article>
          <span>구조변화 탐지 검증</span>
          <strong>{selected.structuralValidation.status === "ready" ? `${Math.round((selected.structuralValidation.persistenceRate ?? 0) * 100)}% 지속` : "자동 대기"}</strong>
          <p>{selected.structuralValidation.note}</p>
        </article>
      </div>

      <div className="housing-period-table-wrap">
        <table className="housing-period-table">
          <thead><tr><th>분석기간</th><th>실제 범위</th><th>관측월</th><th>대표 r</th><th>주요 전이구간</th><th>상태</th></tr></thead>
          <tbody>
            {selected.periods.map((period) => (
              <tr key={period.id} className={period.id === "all" ? "is-baseline" : ""}>
                <td><strong>{period.label}</strong>{period.id === "all" && <small> 기준선</small>}</td>
                <td>{periodRange(period.startMonth, period.endMonth)}</td>
                <td>{period.completeMonths}</td>
                <td>{correlation(period.representative.correlation)}<small>{directionLabel(period.representative.direction)}{period.representative.minimumSamples == null ? "" : ` · lag별 최소 n=${period.representative.minimumSamples}`}</small></td>
                <td>{period.representative.windowLabel ?? "N/A"}{period.representative.bestLagMonths == null ? "" : ` · 최고 |r| ${period.representative.bestLagMonths}M`}</td>
                <td><span className={`period-status period-status--${period.status}`}>{period.status === "ready" ? "사용" : period.status === "limited" ? "주의" : "대기"}</span>{period.warning && <small>{period.warning}</small>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="housing-rolling-grid">
        <article className="housing-rolling-card">
          <header><strong>{selected.rollingWindowMonths}개월 rolling 관계</strong><small>r=0선을 기준으로 관계 방향이 시간에 따라 바뀌는지 확인</small></header>
          {rolling.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={rolling} margin={{ top: 10, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} minTickGap={24} />
                <YAxis domain={[-1, 1]} tick={{ fontSize: 10 }} width={38} />
                <ReferenceLine y={0} stroke="#64748b" strokeDasharray="4 4" />
                <Tooltip formatter={(value: unknown) => [`r=${Number(value).toFixed(2)}`, "대표 관계"]} labelFormatter={(label: unknown) => `${String(label)} 종료 24개월창`} />
                <Line type="monotone" dataKey="correlation" stroke="#0f766e" strokeWidth={2.2} dot={false} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="empty-state">24개월 rolling 관계를 만들 표본이 아직 부족합니다.</div>}
          <p>rolling r도 인과효과가 아닙니다. 기간을 조금 옮겼을 때 관계가 유지되는지와 부호반전 후보를 찾는 용도입니다.</p>
        </article>

        <article className="housing-change-card">
          <header><strong>구조변화 후보</strong><small>데이터 → 정책 순서로 먼저 찾습니다.</small></header>
          {selected.structuralChanges.length ? selected.structuralChanges.map((change) => (
            <div className="housing-change-item" key={`${selected.id}-${change.month}`}>
              <div><b>{change.month}</b><span>{change.kind === "sign_reversal" ? "부호 반전 후보" : "관계 강도 변화"}</span></div>
              <strong>{correlation(change.beforeCorrelation)} → {correlation(change.afterCorrelation)}</strong>
              <p>{change.explanation}</p>
              {change.matchedPolicies.length > 0 ? (
                <details><summary>근처 정책 {change.matchedPolicies.length}건</summary>{change.matchedPolicies.map((policy) => (
                  <div className="housing-policy-match" key={policy.id}>
                    <a href={policy.sourceUrl} target="_blank" rel="noreferrer">{policy.title}</a>
                    <small>{policy.date} · {policyTiming(policy.offsetMonths, policy.dateBasis)} · {policy.status === "confirmed" ? "확정 정책" : "자동수집 후보"}</small>
                  </div>
                ))}</details>
              ) : <small>±6개월 안에서 현재 Registry/Watcher와 연결되는 정책을 찾지 못했습니다.</small>}
            </div>
          )) : <div className="empty-state">현재 기준을 넘는 구조변화 후보가 없습니다. 데이터가 추가되면 매번 자동 재탐지합니다.</div>}
        </article>
      </div>

      <details className="housing-policy-comparison-details">
        <summary>정책 시행 전후 자동비교 보기</summary>
        <div className="housing-policy-comparison-list">
          {selected.policyComparisons.map((comparison) => (
            <article key={`${selected.id}-${comparison.policyId}`}>
              <div><span>{comparison.importance === "major" ? "대정책" : "소정책"} · {comparison.policyStatus === "confirmed" ? "확정" : "자동후보"}</span><strong>{comparison.policyTitle}</strong><small>시행 {comparison.effectiveMonth}</small></div>
              <b>{policyResultLabel(comparison.result)}</b>
              <p>{comparison.explanation}</p>
              <small>전: {comparison.beforePeriod ? periodRange(comparison.beforePeriod.startMonth, comparison.beforePeriod.endMonth) : "N/A"} / 후: {comparison.afterPeriod ? periodRange(comparison.afterPeriod.startMonth, comparison.afterPeriod.endMonth) : "N/A"}</small>
            </article>
          ))}
        </div>
      </details>

      <details className="housing-policy-watch-details">
        <summary>자동수집된 정책 후보 보기</summary>
        <p>금융위·국토부·한국은행 공식 소스만 수집합니다. RSS를 우선하며 국토부 RSS 장애 시 공식 보도자료 목록을 fallback으로 사용합니다. 대정책/소정책 후보와 영향경로를 자동 분류하지만, 미검증 후보가 LTV·DSR·한도 숫자를 변경하지는 않습니다.</p>
        {recentCandidates.length ? recentCandidates.map((item) => (
          <div className="housing-policy-watch-item" key={item.id}>
            <a href={item.link} target="_blank" rel="noreferrer">{item.title}</a>
            <small>{item.provider} · {item.effectiveDate ? `시행후보 ${item.effectiveDate}` : item.announcedDate ?? "날짜 N/A"} · {item.classification === "candidate_major" ? "대정책 후보" : item.classification === "candidate_minor" ? "소정책 후보" : "관찰"}</small>
          </div>
        )) : <div className="empty-state">첫 Auto-Watcher 수집 전이거나 현재 필터에 걸린 정책 후보가 없습니다.</div>}
      </details>

      <p className="housing-h4-footnote"><b>해석 원칙:</b> “정책 이후 관계가 달라졌다”와 “정책 때문에 달라졌다”를 구분합니다. 구조변화보다 나중에 시행된 정책은 원인 후보처럼 표시하지 않고 시간순서만 보여줍니다. 표본·기간·정책국면에 민감하면 경고 또는 판단보류로 낮춥니다. 기간표의 범위는 결과변수 관측월이며, lag 분석에서는 선행변수의 최대 12개월 이전 값이 추가로 사용될 수 있습니다.</p>
    </section>
  );
}
